import React, { useState, useCallback, useEffect, type ReactNode } from 'react';
import { Menu, X } from 'lucide-react';

export interface AppShellBranding {
  /** Short 2-3 letter mark shown in the sidebar logo box */
  mark: string;
  /** Application name (line 1) */
  name: string;
  /** Subtitle (line 2, e.g. "Intelligence") */
  subtitle?: string;
}

export interface AppShellProps {
  /** Branding info for the sidebar header */
  branding?: AppShellBranding;
  /** Sidebar content (below the branding header) */
  sidebar: ReactNode;
  /** Sidebar footer (settings button, etc.) */
  sidebarFooter?: ReactNode;
  /** Main content area */
  children: ReactNode;
  /** Slot for modals rendered on top of everything */
  modals?: ReactNode;
  /** Initial sidebar open state (default: true on desktop) */
  defaultSidebarOpen?: boolean;
}

const defaultBranding: AppShellBranding = {
  mark: 'S',
  name: 'SAGE',
  subtitle: 'Intelligence',
};

export const AppShell: React.FC<AppShellProps> = ({
  branding = defaultBranding,
  sidebar,
  sidebarFooter,
  children,
  modals,
  defaultSidebarOpen,
}) => {
  // Responsive default: closed on narrow viewports, open on wide.
  // Falls back to `defaultSidebarOpen` when explicitly provided.
  const initialOpen = (() => {
    if (typeof defaultSidebarOpen === 'boolean') return defaultSidebarOpen;
    if (typeof window === 'undefined') return true;
    return window.innerWidth >= 768;
  })();
  const [isSidebarOpen, setIsSidebarOpen] = useState(initialOpen);

  // Auto-close the sidebar on narrow viewports after resize (portrait rotation etc.)
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const onResize = () => {
      if (window.innerWidth < 768) setIsSidebarOpen(false);
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const closeSidebarOnMobile = useCallback(() => {
    if (typeof window !== 'undefined' && window.innerWidth < 768) {
      setIsSidebarOpen(false);
    }
  }, []);

  // Escape key closes the sidebar (desktop keyboard users)
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isSidebarOpen) setIsSidebarOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isSidebarOpen]);

  void closeSidebarOnMobile;

  return (
    <div
      className="flex h-screen w-full overflow-hidden"
      style={{
        backgroundColor: 'var(--sage-bg)',
        color: 'var(--sage-text)',
        fontFamily: 'var(--sage-font-sans)',
      }}
    >
      {/* ALWAYS-VISIBLE top-left toggle — escape hatch regardless of sidebar state.
          z-[70] beats sidebar (z-60), backdrop (z-50), and any chat-internal overlays. */}
      <button
        onClick={() => setIsSidebarOpen((v) => !v)}
        className="fixed top-3 left-3 z-[70] p-2 rounded-lg transition-all shadow-lg border"
        style={{
          backgroundColor: 'var(--sage-surface, #1a1a1b)',
          borderColor: 'var(--sage-white-10, rgba(255,255,255,0.1))',
          color: 'var(--sage-text, #fff)',
        }}
        aria-label={isSidebarOpen ? 'Close sidebar' : 'Open sidebar'}
        title={isSidebarOpen ? 'Close sidebar (Esc)' : 'Open sidebar'}
      >
        {isSidebarOpen ? <X size={18} /> : <Menu size={18} />}
      </button>

      {/* Backdrop — shown under sidebar on mobile, tap to close. z-50. */}
      {isSidebarOpen && (
        <div
          className="fixed inset-0 z-50 md:hidden"
          style={{ backgroundColor: 'rgba(0,0,0,0.6)' }}
          onClick={() => setIsSidebarOpen(false)}
          aria-hidden="true"
        />
      )}

      {/* Sidebar — z-60 so it always sits above main content + backdrop + chat internals. */}
      <div
        className={`fixed md:relative top-0 left-0 z-[60] h-full transition-all duration-300 border-r border-[var(--sage-white-5)] ${
          isSidebarOpen
            ? 'w-[280px]'
            : 'w-0 -translate-x-full md:w-0 md:-translate-x-full pointer-events-none overflow-hidden'
        }`}
        style={{ backgroundColor: 'var(--sage-sidebar)' }}
      >
        <div className="flex flex-col h-full select-none">
          {/* Branding Header */}
          <div className="p-5 pb-4">
            <div className="flex items-center gap-3 mb-6">
              <div
                className="w-8 h-8 rounded-lg flex items-center justify-center text-sm font-bold tracking-widest border"
                style={{
                  backgroundColor: 'color-mix(in srgb, var(--sage-primary) 20%, transparent)',
                  borderColor: 'color-mix(in srgb, var(--sage-primary) 30%, transparent)',
                  color: 'var(--sage-primary)',
                  fontFamily: 'var(--sage-font-serif)',
                }}
              >
                {branding.mark}
              </div>
              <div className="flex flex-col flex-1 min-w-0">
                <span
                  className="font-semibold text-base tracking-wide leading-tight"
                  style={{ color: 'var(--sage-text)', fontFamily: 'var(--sage-font-serif)' }}
                >
                  {branding.name}
                </span>
                {branding.subtitle && (
                  <span
                    className="text-[10px] uppercase tracking-[0.15em]"
                    style={{ color: 'var(--sage-primary)', fontFamily: 'var(--sage-font-sans)' }}
                  >
                    {branding.subtitle}
                  </span>
                )}
              </div>
              {/* Close is handled by the fixed top-left toggle button (z-70). */}
            </div>
          </div>

          {/* Scrollable sidebar content */}
          <div className="flex-1 overflow-y-auto px-3 sage-scrollbar-hide">
            {sidebar}
          </div>

          {/* Footer */}
          {sidebarFooter && (
            <div className="p-3 border-t border-[var(--sage-white-5)]">{sidebarFooter}</div>
          )}
        </div>
      </div>

      {/* Main Content — top padding clears the fixed hamburger toggle on mobile. */}
      <div
        className="flex-1 flex flex-col h-full relative min-w-0"
        style={{ backgroundColor: 'var(--sage-bg)', paddingTop: isSidebarOpen ? '0' : '52px' }}
      >
        {/* Expose closeSidebarOnMobile via context or just pass children */}
        {children}
      </div>

      {/* Modal slot */}
      {modals}
    </div>
  );
};

/**
 * Context for child components to close the sidebar on mobile after navigation.
 * Usage pattern:
 *   <AppShell sidebar={<MySidebar onNavigate={closeMobileSidebar} />}>
 */
export type { AppShellProps as AppShellComponentProps };
